import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import moment from 'moment';

import {Button} from 'shells/button/button';
import {ButtonMover} from 'shells/button-mover/button-mover';
import {Checkbox} from 'shells/checkbox/checkbox';
import {ConditionalCheckboxInput} from 'shells/form/conditional-checkbox-input';
import {FormField} from 'shells/form/form-field';
import {HelpButton} from 'shells/help/help-button';
import {Link} from 'shells/link/link';
import {PopoverInfo} from 'shells/popover-info/popover-info';
import {useDidMountEffect} from 'shells/utils/hooks/use-did-mount-effect';

import type {
    EditionUpdateInput,
    DripSequenceFragment as DripSequence,
    EditionFragment as DripEdition,
    GetVerifiedEmailAddresses_fromAddresses_edges_node as FromAddress,
} from 'nutshell-graphql-types';

import {GraphQLSelectMarketingEmails} from '../../../core/select/graphql-select-marketing-emails';

import {UtmFormModal} from '../../editions/editions-details/utm-modals/utm-form-modal';
import {UtmHelpModal} from '../../editions/editions-details/utm-modals/utm-help-modal';
import {PreviewData} from '../../editions/prepare-to-send-modal/preview-data';

import styles from './drip-sequences-details-form.module.css';

type Props = {
    dripSequence: DripSequence;
    onDripSave: (saveData: any) => Promise<any>;
    editions: DripEdition[];
    onEditionUpdate: (saveData: EditionUpdateInput) => Promise<any>;
};

export function DripSequencesDetailsForm(props: Props) {
    const [fromEmailAddress, setFromEmailAddress] = React.useState<FromAddress | null | undefined>(
        props.dripSequence.from || null
    );

    const [isBusinessHoursOnly, setIsBusinessHoursOnly] = React.useState<boolean>(
        props.dripSequence.isBusinessHoursOnly
    );

    const [useUtmParams, setUseUtmParams] = React.useState<boolean>(
        props.dripSequence.useUtmParameters
    );
    const [showUtmHelpModal, setShowUtmHelpModal] = React.useState<boolean>(false);
    const [showUtmFormModal, setShowUtmFormModal] = React.useState<boolean>(false);

    useDidMountEffect(() => {
        props.onDripSave({
            id: props.dripSequence.id,
            isBusinessHoursOnly: isBusinessHoursOnly,
            fromAddressId: fromEmailAddress ? fromEmailAddress.id : undefined,
            useUtmParameters: useUtmParams,
        });
    }, [props.dripSequence.id, isBusinessHoursOnly, fromEmailAddress, useUtmParams]);

    const showPreviewStatuses = ['SENDING', 'SENT', 'HALTED', 'IN_REVIEW'];
    const showPreviewMode = showPreviewStatuses.includes(props.dripSequence.status);

    return (
        <React.Fragment>
            <form
                onSubmit={(event) => {
                    event.preventDefault();

                    props.onDripSave({
                        id: props.dripSequence.id,
                        isBusinessHoursOnly: isBusinessHoursOnly,
                        fromAddressId: fromEmailAddress ? fromEmailAddress.id : undefined,
                        useUtmParameters: useUtmParams,
                    });
                }}
                className={styles.container}
            >
                {showPreviewMode ? (
                    // this will change or we can make a new preview data component for drips
                    // @ts-expect-error
                    <PreviewData edition={props.dripSequence.editions[0]} />
                ) : (
                    <div>
                        <FormField
                            tooltipInfo={
                                <div>
                                    This will appear to your recipients as the “from” name and email
                                    address. Admins can manage these email addresses in the{' '}
                                    <Link
                                        to='/email-marketing/sender-address'
                                        // @ts-expect-error
                                        as={RoutedLink}
                                        size='small'
                                        variant='primary'
                                    >
                                        Sender address settings.
                                    </Link>
                                </div>
                            }
                            innerId='from'
                            label='From'
                            helperText={
                                props.dripSequence.status === 'DRAFT' &&
                                fromEmailAddress &&
                                fromEmailAddress.status !== 'VERIFIED' ? (
                                    <div className='flex justify-sb align-center'>
                                        <span className={styles['unverified-helper-text']}>
                                            This email address is not verified yet.
                                        </span>
                                        <Link
                                            to='/email-marketing/sender-address'
                                            // @ts-expect-error
                                            as={RoutedLink}
                                            size='small'
                                            variant='primary'
                                        >
                                            Manage senders
                                        </Link>
                                    </div>
                                ) : undefined
                            }
                        >
                            <GraphQLSelectMarketingEmails
                                selectedId={fromEmailAddress ? fromEmailAddress.id : null}
                                onChange={(emailAddress: FromAddress) => {
                                    setFromEmailAddress(emailAddress);
                                }}
                                autoFocus={false}
                                onlyIncludeVerified={props.dripSequence.status !== 'DRAFT'}
                            />
                        </FormField>
                        <FormField innerId='businessHoursOnly'>
                            <Checkbox
                                checked={isBusinessHoursOnly}
                                label='Only send messages during business hours'
                                onChange={(newValue) => {
                                    setIsBusinessHoursOnly(newValue);
                                }}
                                helpComponent={
                                    <PopoverInfo
                                        info={getBusinessHoursTooltipContent()}
                                        size={18}
                                        bodyColor='dark'
                                    />
                                }
                                isLarge={true}
                            />
                        </FormField>
                        <FormField innerId='useUtmParameters'>
                            <ConditionalCheckboxInput
                                checked={useUtmParams}
                                label='Add UTM parameters to all links'
                                onChange={(newValue) => {
                                    setUseUtmParams(newValue);
                                }}
                                helpComponent={
                                    <HelpButton
                                        // @ts-expect-error
                                        size={18}
                                        onClick={() => {
                                            setShowUtmHelpModal(true);
                                        }}
                                    />
                                }
                            >
                                <ButtonMover direction={['left', 'up']} size='big'>
                                    <Button
                                        variant='text-primary'
                                        size='big'
                                        onClick={() => {
                                            setShowUtmFormModal(true);
                                        }}
                                    >
                                        Edit parameters
                                    </Button>
                                </ButtonMover>
                            </ConditionalCheckboxInput>
                        </FormField>
                    </div>
                )}
            </form>
            <UtmHelpModal
                isOpen={showUtmHelpModal}
                onClose={() => {
                    setShowUtmHelpModal(false);
                }}
            />
            <UtmFormModal
                isOpen={showUtmFormModal}
                onClose={() => {
                    setShowUtmFormModal(false);
                }}
                editions={props.editions}
                source={props.dripSequence.utmParameters.source}
                medium={props.dripSequence.utmParameters.medium}
                onSave={(source, medium, editionUtmCampaigns) => {
                    const promises = [];

                    promises.push(
                        props.onDripSave({
                            id: props.dripSequence.id,
                            utmSource: source,
                            utmMedium: medium,
                        })
                    );

                    editionUtmCampaigns.forEach((edition) => {
                        promises.push(
                            props.onEditionUpdate({
                                editionId: edition.id,
                                utmCampaign: edition.useDefaultCampaignValue
                                    ? null
                                    : edition.utmCampaign,
                            })
                        );
                    });

                    return Promise.all(promises);
                }}
            />
        </React.Fragment>
    );
}

function getBusinessHoursTooltipContent() {
    const businessHoursStart = moment(
        NutshellFrontendSettings.businessHours.start,
        NutshellFrontendSettings.businessHours.format
    )
        .format('h:mma')
        .toString();

    const businessHoursEnd = moment(
        NutshellFrontendSettings.businessHours.end,
        NutshellFrontendSettings.businessHours.format
    )
        .format('h:mma')
        .toString();

    return (
        <>
            Only send drip messages during your team’s business hours:{' '}
            <b>
                M-F, {businessHoursStart} - {businessHoursEnd}
            </b>
        </>
    );
}
